'use client';

import { type MouseEventHandler, forwardRef, useCallback, useContext, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { useControllableState } from '../../hooks/use-controllable-state';
import { useDisabledState } from '../../hooks/use-disabled-state';
import { Slot, Slottable } from '../slot';
import { ToggleButtonGroupContext } from '../toggle-button-group/context/toggle-button-group.context';
import { ToggleButtonRootContext } from './context/toggle-button-root.context';
import { toggleButtonRecipe } from './styles/toggle-button-root.styles';
import { ToggleButtonLoading } from './toggle-button-loading';
import type { ToggleButtonRootPropsInterface } from './types/toggle-button-root.types';

/**
 * @since 10.0.0
 */
export const ToggleButtonRoot = forwardRef<HTMLButtonElement, ToggleButtonRootPropsInterface>(
  (props: ToggleButtonRootPropsInterface, forwardedRef) => {
    const { value, loading = false, ...restProps } = props;
    const {
      value: contextValue,
      defaultValue: contextDefaultValue,
      setValue: setContextValue,
      size: contextSize,
      disabled: contextDisabled,
    } = useContext(ToggleButtonGroupContext);

    const memoPressed = useMemo(() => {
      if (value && contextValue) {
        return Array.isArray(contextValue) ? contextValue?.includes(value) : value === contextValue;
      }
      return undefined;
    }, [value, contextValue]);

    const memoDefaultSelected = useMemo(() => {
      if (value && contextDefaultValue) {
        return Array.isArray(contextDefaultValue)
          ? contextDefaultValue?.includes(value)
          : value === contextDefaultValue;
      }
      return undefined;
    }, [value, contextDefaultValue]);

    const {
      asChild = false,
      children,
      className,
      css: cssProp,
      disabled = contextDisabled ?? false,
      size = contextSize || 'sm',
      selected = memoPressed,
      defaultSelected = memoDefaultSelected,
      onSelectedChange,
      onClick,
      ...rest
    } = restProps;

    const [selectedState, setSelectedState] = useControllableState({
      prop: selected,
      defaultProp: defaultSelected,
      onChange: onSelectedChange,
    });

    const isDisabled = disabled || loading;

    const updateContextValue = useCallback(() => {
      if (!setContextValue || !value) return;

      if (Array.isArray(contextValue)) {
        const valueIsInContext = contextValue?.includes(value);
        const newValue = valueIsInContext
          ? contextValue?.filter((v) => v !== value)
          : [...(contextValue || []), value];

        setContextValue(newValue);
      } else {
        setContextValue(value);
      }
    }, [value, contextValue, setContextValue]);

    const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
      (e) => {
        if (!isDisabled) {
          onClick?.(e);
          updateContextValue();
          setSelectedState((prev) => !prev);
        }
      },
      [setSelectedState, isDisabled, onClick, updateContextValue],
    );

    const styles = css(
      toggleButtonRecipe.raw({
        disabled,
        size: contextSize || size,
        selected: !!selectedState,
        loading,
      }),
      cssProp,
    );
    const Component = asChild ? Slot : 'button';

    const a11yDisabledState = useDisabledState({ disabled: isDisabled, focusable: true });

    return (
      <ToggleButtonRootContext.Provider
        value={{
          disabled,
          size: contextSize || size,
          selected: selectedState,
          loading,
        }}
      >
        <Component
          ref={forwardedRef}
          aria-pressed={!!selectedState}
          data-c11n-component="ToggleButton.Root"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', 'group', styles, className)}
          onClick={handleClick}
          type={asChild ? undefined : 'button'}
          {...rest}
          {...a11yDisabledState}
        >
          <Slottable>{children}</Slottable>
          {loading ? <ToggleButtonLoading /> : null}
        </Component>
      </ToggleButtonRootContext.Provider>
    );
  },
);

ToggleButtonRoot.displayName = 'ToggleButton.Root';
